import { z } from 'zod';

/**
 * Wave 4 enums: meetings and transcripts (SPEC §7.7, PLAN §3.2 "Meetings", "Transcripts and the
 * import adapter", D-850 ff.). The CHECK constraints of the tables in
 * packages/db/src/schema/meetings.ts are built from these lists.
 */

// ---------------------------------------------------------------- series and templates

/**
 * The six PTSTDM meeting types (Phase 7, D-850): they keep their code paths, their call path, the
 * admin and manager-chain reads of D-853 and every KPI and health computation.
 */
export const PTSTDM_MEETING_TYPES = [
  'one_on_one',
  'dept_tactical',
  'leadership_weekly',
  'cross_team',
  'monthly',
  'quarterly',
] as const;
export type PtstdmMeetingType = (typeof PTSTDM_MEETING_TYPES)[number];
export const ptstdmMeetingTypeSchema = z.enum(PTSTDM_MEETING_TYPES);

/**
 * The calendar meeting types (wave 5, D-1002): `general` (any meeting, client meetings and booked
 * appointments included) and `webinar` (one-off, D-1004). They come from `POST /meetings` and
 * `POST /webinars`, never from `POST /meeting-series`; they do not inherit D-853's admin-sees-all
 * or manager-chain reads (D-1006), and no KPI, health or scorecard scope counts them.
 */
export const CALENDAR_MEETING_TYPES = ['general', 'webinar'] as const;
export type CalendarMeetingType = (typeof CALENDAR_MEETING_TYPES)[number];
export const calendarMeetingTypeSchema = z.enum(CALENDAR_MEETING_TYPES);

/** `meeting_series.type` and `meeting_agenda_templates.meeting_type`: PTSTDM types, then calendar types. */
export const MEETING_TYPES = [...PTSTDM_MEETING_TYPES, ...CALENDAR_MEETING_TYPES] as const;
export type MeetingType = (typeof MEETING_TYPES)[number];
export const meetingTypeSchema = z.enum(MEETING_TYPES);

/** True for `general` and `webinar` (D-1002): the calendar rules apply, not D-853's. */
export function isCalendarMeetingType(type: string): type is CalendarMeetingType {
  return (CALENDAR_MEETING_TYPES as readonly string[]).includes(type);
}

/** True for the six PTSTDM types. */
export function isPtstdmMeetingType(type: string): type is PtstdmMeetingType {
  return (PTSTDM_MEETING_TYPES as readonly string[]).includes(type);
}

/** `meeting_series.non_working_day_rule`: what a slot on a holiday or day off becomes. */
export const MEETING_NON_WORKING_DAY_RULES = [
  'previous_working_day',
  'next_working_day',
  'skip',
  'keep',
] as const;
export type MeetingNonWorkingDayRule = (typeof MEETING_NON_WORKING_DAY_RULES)[number];
export const meetingNonWorkingDayRuleSchema = z.enum(MEETING_NON_WORKING_DAY_RULES);

/** `meeting_series.visibility`: default `attendees` for 1:1s, otherwise `channel`. */
export const MEETING_VISIBILITIES = ['attendees', 'channel'] as const;
export type MeetingVisibility = (typeof MEETING_VISIBILITIES)[number];
export const meetingVisibilitySchema = z.enum(MEETING_VISIBILITIES);

/** `meeting_series.status`. */
export const MEETING_SERIES_STATUSES = ['active', 'paused', 'ended'] as const;
export type MeetingSeriesStatus = (typeof MEETING_SERIES_STATUSES)[number];
export const meetingSeriesStatusSchema = z.enum(MEETING_SERIES_STATUSES);

/**
 * `meeting_series_attendees.role`. `presenter` and `attendee` exist only on webinars (wave 5,
 * D-1031); `required` and `optional` never on a webinar (trigger `meeting_series_attendees_role_guard`,
 * 0029). `attendee` rows are the webinar **audience**: they make the meeting visible and "mine",
 * never a participant (D-1006).
 */
export const MEETING_ATTENDEE_ROLES = [
  'chair',
  'required',
  'optional',
  'presenter',
  'attendee',
] as const;
export type MeetingAttendeeRole = (typeof MEETING_ATTENDEE_ROLES)[number];
export const meetingAttendeeRoleSchema = z.enum(MEETING_ATTENDEE_ROLES);

/** The roles of every non-webinar series (the PTSTDM types and `general`). */
export const GENERAL_ATTENDEE_ROLES = [
  'chair',
  'required',
  'optional',
] as const satisfies readonly MeetingAttendeeRole[];
/** The roles of a webinar series: the chair, internal presenters and the internal audience. */
export const WEBINAR_ATTENDEE_ROLES = [
  'chair',
  'presenter',
  'attendee',
] as const satisfies readonly MeetingAttendeeRole[];

/**
 * `meeting_series.non_working_day_rule` values a calendar type allows (D-1004): iCalendar never has
 * to express a holiday shift (`meeting_series_calendar_rule_check`).
 */
export const CALENDAR_NON_WORKING_DAY_RULES = [
  'keep',
  'skip',
] as const satisfies readonly MeetingNonWorkingDayRule[];
export const calendarNonWorkingDayRuleSchema = z.enum(CALENDAR_NON_WORKING_DAY_RULES);

/** `meeting_series.lobby_policy` (D-1030): every guest waits, or invited guests join once a member is in. */
export const MEETING_LOBBY_POLICIES = ['guests_wait', 'invited_guests_join'] as const;
export type MeetingLobbyPolicy = (typeof MEETING_LOBBY_POLICIES)[number];
export const meetingLobbyPolicySchema = z.enum(MEETING_LOBBY_POLICIES);

// ---------------------------------------------------------------- occurrences

/** `meeting_occurrences.status`. */
export const MEETING_OCCURRENCE_STATUSES = [
  'scheduled',
  'in_progress',
  'concluded',
  'cancelled',
  'missed',
] as const;
export type MeetingOccurrenceStatus = (typeof MEETING_OCCURRENCE_STATUSES)[number];
export const meetingOccurrenceStatusSchema = z.enum(MEETING_OCCURRENCE_STATUSES);

/** `meeting_agenda_items.source_type`: where an auto-populated item came from. */
export const MEETING_AGENDA_SOURCE_TYPES = [
  'manual',
  'kpi_off_track',
  'kpi_at_risk',
  'open_task',
  'task_slip',
  'waiting_between',
  'previous_action_item',
  'open_decision',
  'issue',
  'weekly_review',
  'department_health',
  'meeting_health',
] as const;
export type MeetingAgendaSourceType = (typeof MEETING_AGENDA_SOURCE_TYPES)[number];
export const meetingAgendaSourceTypeSchema = z.enum(MEETING_AGENDA_SOURCE_TYPES);

/** `meeting_agenda_items.status`. */
export const MEETING_AGENDA_ITEM_STATUSES = [
  'pending',
  'discussed',
  'deferred',
  'resolved',
] as const;
export type MeetingAgendaItemStatus = (typeof MEETING_AGENDA_ITEM_STATUSES)[number];
export const meetingAgendaItemStatusSchema = z.enum(MEETING_AGENDA_ITEM_STATUSES);

/** `meeting_issues.status`. */
export const MEETING_ISSUE_STATUSES = ['open', 'solved', 'dropped'] as const;
export type MeetingIssueStatus = (typeof MEETING_ISSUE_STATUSES)[number];
export const meetingIssueStatusSchema = z.enum(MEETING_ISSUE_STATUSES);

/** `meeting_notes.origin` and `meeting_decisions.origin`: typed by a person, or an approved AI draft. */
export const MEETING_CONTENT_ORIGINS = ['manual', 'ai_draft'] as const;
export type MeetingContentOrigin = (typeof MEETING_CONTENT_ORIGINS)[number];
export const meetingContentOriginSchema = z.enum(MEETING_CONTENT_ORIGINS);

/** `meeting_action_items.link_kind`: a task created in the meeting, or an existing one linked. */
export const MEETING_ACTION_LINK_KINDS = ['created', 'linked_existing'] as const;
export type MeetingActionLinkKind = (typeof MEETING_ACTION_LINK_KINDS)[number];
export const meetingActionLinkKindSchema = z.enum(MEETING_ACTION_LINK_KINDS);

/** `meeting_attendance.status`. */
export const MEETING_ATTENDANCE_STATUSES = [
  'expected',
  'present',
  'late',
  'absent',
  'excused',
] as const;
export type MeetingAttendanceStatus = (typeof MEETING_ATTENDANCE_STATUSES)[number];
export const meetingAttendanceStatusSchema = z.enum(MEETING_ATTENDANCE_STATUSES);

/** `meeting_attendance.source`: filled by the system, marked by hand, or from the linked call. */
export const MEETING_ATTENDANCE_SOURCES = ['system', 'manual', 'call'] as const;
export type MeetingAttendanceSource = (typeof MEETING_ATTENDANCE_SOURCES)[number];
export const meetingAttendanceSourceSchema = z.enum(MEETING_ATTENDANCE_SOURCES);

// ---------------------------------------------------------------- transcripts

/** `transcripts.adapter`: `plaud_api` is reserved for a later pull-based source. */
export const TRANSCRIPT_ADAPTERS = ['plaud_export', 'plain_text', 'plaud_api'] as const;
export type TranscriptAdapter = (typeof TRANSCRIPT_ADAPTERS)[number];
export const transcriptAdapterSchema = z.enum(TRANSCRIPT_ADAPTERS);

/** `transcripts.input_format`. */
export const TRANSCRIPT_INPUT_FORMATS = ['txt', 'srt', 'vtt', 'json', 'paste'] as const;
export type TranscriptInputFormat = (typeof TRANSCRIPT_INPUT_FORMATS)[number];
export const transcriptInputFormatSchema = z.enum(TRANSCRIPT_INPUT_FORMATS);

/** `transcripts.status`. */
export const TRANSCRIPT_STATUSES = ['parsed', 'extracting', 'extracted', 'failed'] as const;
export type TranscriptStatus = (typeof TRANSCRIPT_STATUSES)[number];
export const transcriptStatusSchema = z.enum(TRANSCRIPT_STATUSES);

/** Transcript text limit (PLAN "Adapter interface"): 1 MB, taken as 1 MiB (also a CHECK on `transcripts`). */
export const TRANSCRIPT_MAX_BYTES = 1_048_576;

// ---------------------------------------------------------------- the seeded agenda templates

/** One section of an agenda template (`meeting_agenda_templates.sections[]`, PLAN §3.2). */
export interface AgendaTemplateSectionSpec {
  key: string;
  title: string;
  timeboxMinutes?: number;
  /** Which sources fill this section automatically; empty for a manual section. */
  autoSources: readonly MeetingAgendaSourceType[];
}

export interface SystemAgendaTemplateSpec {
  /** Stable key: seeds and services derive the deterministic ids and the one-per-type rule from it. */
  key: MeetingType;
  name: string;
  meetingType: MeetingType;
  /** The fixed departmental tactical agenda: sections cannot be added, removed or reordered. */
  isLocked: boolean;
  sections: readonly AgendaTemplateSectionSpec[];
}

/**
 * The system templates every workspace has, with the sections in order and the auto-population
 * sources of each (PLAN §3.2 "meeting_agenda_templates"). `dept_tactical` is the locked fixed agenda
 * (SPEC §7.7). Timeboxes are suggestions a series may tune (not for the locked agenda's order).
 */
export const SYSTEM_AGENDA_TEMPLATES: readonly SystemAgendaTemplateSpec[] = Object.freeze([
  {
    key: 'one_on_one',
    name: 'One-on-one',
    meetingType: 'one_on_one',
    isLocked: false,
    sections: [
      { key: 'checkin', title: 'Check-in', timeboxMinutes: 3, autoSources: [] },
      {
        key: 'scorecard',
        title: 'Scorecard',
        timeboxMinutes: 5,
        autoSources: ['kpi_off_track', 'kpi_at_risk'],
      },
      {
        key: 'weekly_review',
        title: 'Weekly review',
        timeboxMinutes: 5,
        autoSources: ['weekly_review'],
      },
      {
        key: 'tasks',
        title: 'Tasks',
        timeboxMinutes: 7,
        autoSources: ['open_task', 'task_slip', 'waiting_between'],
      },
      { key: 'decisions', title: 'Decisions', timeboxMinutes: 3, autoSources: ['open_decision'] },
      {
        key: 'previous_actions',
        title: 'Previous actions',
        timeboxMinutes: 4,
        autoSources: ['previous_action_item'],
      },
      { key: 'commitments', title: 'Commitments', timeboxMinutes: 3, autoSources: [] },
    ],
  },
  {
    key: 'dept_tactical',
    name: 'Departmental tactical (fixed agenda)',
    meetingType: 'dept_tactical',
    isLocked: true,
    sections: [
      {
        key: 'scorecard',
        title: 'Scorecard',
        timeboxMinutes: 5,
        autoSources: ['kpi_off_track', 'kpi_at_risk'],
      },
      {
        key: 'kra_priority_review',
        title: 'KRA and priority review',
        timeboxMinutes: 5,
        autoSources: ['open_task', 'task_slip'],
      },
      { key: 'headlines', title: 'Headlines', timeboxMinutes: 5, autoSources: [] },
      { key: 'todos', title: 'To-dos', timeboxMinutes: 5, autoSources: ['previous_action_item'] },
      {
        key: 'issues',
        title: 'Issues',
        timeboxMinutes: 30,
        autoSources: ['issue', 'open_decision'],
      },
      { key: 'conclude', title: 'Conclude', timeboxMinutes: 5, autoSources: [] },
    ],
  },
  {
    key: 'leadership_weekly',
    name: 'Leadership weekly',
    meetingType: 'leadership_weekly',
    isLocked: false,
    sections: [
      {
        key: 'department_health',
        title: 'Department health',
        timeboxMinutes: 10,
        autoSources: ['department_health'],
      },
      { key: 'scorecard', title: 'Scorecard', timeboxMinutes: 10, autoSources: ['kpi_off_track'] },
      { key: 'slipping', title: 'Slipping tasks', timeboxMinutes: 10, autoSources: ['task_slip'] },
      { key: 'decisions', title: 'Decisions', timeboxMinutes: 10, autoSources: ['open_decision'] },
      { key: 'todos', title: 'To-dos', timeboxMinutes: 5, autoSources: ['previous_action_item'] },
      { key: 'issues', title: 'Issues', timeboxMinutes: 40, autoSources: ['issue'] },
      { key: 'conclude', title: 'Conclude', timeboxMinutes: 5, autoSources: [] },
    ],
  },
  {
    key: 'cross_team',
    name: 'Cross-team sync',
    meetingType: 'cross_team',
    isLocked: false,
    sections: [
      { key: 'todos', title: 'To-dos', timeboxMinutes: 5, autoSources: ['previous_action_item'] },
      {
        key: 'project_status',
        title: 'Project status',
        timeboxMinutes: 15,
        autoSources: ['open_task', 'task_slip'],
      },
      { key: 'decisions', title: 'Decisions', timeboxMinutes: 10, autoSources: ['open_decision'] },
      { key: 'issues', title: 'Issues', timeboxMinutes: 25, autoSources: ['issue'] },
      { key: 'conclude', title: 'Conclude', timeboxMinutes: 5, autoSources: [] },
    ],
  },
  {
    key: 'monthly',
    name: 'Monthly review',
    meetingType: 'monthly',
    isLocked: false,
    sections: [
      {
        key: 'scorecard',
        title: 'Scorecard',
        timeboxMinutes: 20,
        autoSources: ['kpi_off_track', 'kpi_at_risk'],
      },
      {
        key: 'department_health',
        title: 'Department health',
        timeboxMinutes: 15,
        autoSources: ['department_health'],
      },
      {
        key: 'meeting_health',
        title: 'Meeting health',
        timeboxMinutes: 10,
        autoSources: ['meeting_health'],
      },
      { key: 'issues', title: 'Issues', timeboxMinutes: 30, autoSources: ['issue'] },
      { key: 'todos', title: 'To-dos', timeboxMinutes: 10, autoSources: ['previous_action_item'] },
      { key: 'conclude', title: 'Conclude', timeboxMinutes: 5, autoSources: [] },
    ],
  },
  {
    key: 'quarterly',
    name: 'Quarterly review',
    meetingType: 'quarterly',
    isLocked: false,
    sections: [
      {
        key: 'scorecard',
        title: 'Scorecard',
        timeboxMinutes: 30,
        autoSources: ['kpi_off_track', 'kpi_at_risk'],
      },
      {
        key: 'department_health',
        title: 'Department health',
        timeboxMinutes: 20,
        autoSources: ['department_health'],
      },
      {
        key: 'meeting_health',
        title: 'Meeting health',
        timeboxMinutes: 10,
        autoSources: ['meeting_health'],
      },
      { key: 'kra_review', title: 'KRA review', timeboxMinutes: 30, autoSources: [] },
      { key: 'issues', title: 'Issues', timeboxMinutes: 40, autoSources: ['issue'] },
      { key: 'todos', title: 'To-dos', timeboxMinutes: 10, autoSources: ['previous_action_item'] },
      { key: 'conclude', title: 'Conclude', timeboxMinutes: 5, autoSources: [] },
    ],
  },
  // Wave 5 (D-1003): the calendar types' templates, so `agenda_template_id` stays NOT NULL and the
  // `meeting_series_template_type` trigger holds for every series.
  {
    key: 'general',
    name: 'General meeting',
    meetingType: 'general',
    isLocked: false,
    sections: [
      { key: 'agenda', title: 'Agenda', autoSources: [] },
      { key: 'previous_actions', title: 'Previous actions', autoSources: ['previous_action_item'] },
    ],
  },
  {
    key: 'webinar',
    name: 'Webinar',
    meetingType: 'webinar',
    isLocked: false,
    sections: [
      { key: 'run_of_show', title: 'Run of show', autoSources: [] },
      { key: 'qna', title: 'Questions and answers', autoSources: [] },
    ],
  },
] satisfies readonly SystemAgendaTemplateSpec[]);
